import {
  BedEnum,
  DialingCodeEnum,
  DayFormatEnum,
  ImageVariantEnum,
} from "@booking/enums";
import type { BedType, DialingCodeType } from "@booking/types";

import { IconEnum, ScreenWidthEnum } from "@/enums";
import { ImagesMetrics } from "@/types";

import { BOOKING } from "./paths";

export const AIRBRAKE = Object.freeze({
  PROJECT_ID: 532054,
  PROJECT_KEY: "ca0cb734f2fd1ea6bfb9c63499b2057c",
});

export const BED_VARIANTS: Record<BedEnum, BedType> = Object.freeze({
  [BedEnum.TWIN]: {
    icon: IconEnum.BED_TWIN,
    name: "Twin beds",
  },
  [BedEnum.DOUBLE]: {
    icon: IconEnum.BED_DOUBLE,
    name: "Double bed",
  },
});

export const DIALING_CODES: Record<DialingCodeEnum, DialingCodeType> =
  Object.freeze({
    [DialingCodeEnum.UK]: { prefix: "+44" },
    [DialingCodeEnum.US]: { prefix: "+1" },
    [DialingCodeEnum.AUSTRALIA]: { prefix: "+61" },
    [DialingCodeEnum.CANADA]: { prefix: "+1" },
    [DialingCodeEnum.FINLAND]: { prefix: "+358" },
    [DialingCodeEnum.FRANCE]: { prefix: "+33" },
    [DialingCodeEnum.GERMANY]: { prefix: "+49" },
    [DialingCodeEnum.IRELAND]: { prefix: "+353" },
    [DialingCodeEnum.INDIA]: { prefix: "+91" },
    [DialingCodeEnum.ICELAND]: { prefix: "+354" },
    [DialingCodeEnum.ITALY]: { prefix: "+39" },
    [DialingCodeEnum.NETHERLANDS]: { prefix: "+31" },
    [DialingCodeEnum.NORWAY]: { prefix: "+47" },
    [DialingCodeEnum.PORTUGAL]: { prefix: "+351" },
    [DialingCodeEnum.SOUTH_AFRICA]: { prefix: "+27" },
    [DialingCodeEnum.SPAIN]: { prefix: "+34" },
    [DialingCodeEnum.SWEDEN]: { prefix: "+46" },
    [DialingCodeEnum.SWITZERLAND]: { prefix: "+41" },
    [DialingCodeEnum.TURKEY]: { prefix: "+90" },
    [DialingCodeEnum.UAE]: { prefix: "+971" },
  } as const);

export const CUSTOMER_SERVICE = Object.freeze({
  EMAIL: "cs@yourgolftravel.com",
  FIRSTNAME: "Dan",
  IMAGE:
    "https://ygt-res.cloudinary.com/image/upload/ar_1:1,b_rgb:000000,c_fill,g_auto,o_100,r_max,w_400/v1716544466/Staff/Daniel-Scoble-medium_ufqjaz.jpg",
  LASTNAME: "Scoble",
  TELEPHONE: "(+44) 020 7157 1555",
  TELEPHONE_HREF: "00442071571555",
  TELEPHONE_LOCAL: "020 7157 1553",
} as const);

export const DAY_VALUES = Object.freeze({
  [DayFormatEnum.SHORT]: ["S", "M", "T", "W", "T", "F", "S"],
  [DayFormatEnum.MEDIUM]: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
  [DayFormatEnum.LONG]: [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ],
});

export const DATE_FORMATS = Object.freeze({
  DMY: "dd/MM/yyyy",
  HOURS_MINUTES: "HH:mm",
  ISO: "yyyy-MM-dd",
  ORDINAL_DAY_SHORT_MONTH: "do MMM",
  ORDINAL_DAY_LONG_MONTH: "EEEE do MMMM",
  ORDINAL_LONG_MONTH_YEAR: "do MMMM yyyy",
  SHORT_ORDINAL_DAY_SHORT_MONTH: "E do MMM",
} as const);

export const IMAGE_BUCKET =
  "https://storage.googleapis.com/public.yourgolftravel.com/images";

export const IMAGES_METRICS: ImagesMetrics = {
  [ImageVariantEnum.NONE]: {
    [ScreenWidthEnum.BASE]: { width: 184 },
    [ScreenWidthEnum.MOBILE]: { width: 256 },
    [ScreenWidthEnum.TABLET]: { width: 384 },
    [ScreenWidthEnum.DESKTOP]: { width: 512 },
  },
  [ImageVariantEnum.BANNER]: {
    [ScreenWidthEnum.BASE]: { width: 320 },
    [ScreenWidthEnum.MOBILE]: { width: 800 },
    [ScreenWidthEnum.TABLET]: { width: 1200 },
    [ScreenWidthEnum.DESKTOP]: { width: 1920 },
  },
  [ImageVariantEnum.CARD]: {
    [ScreenWidthEnum.BASE]: { height: 240, width: 240 },
    [ScreenWidthEnum.MOBILE]: { height: 320, width: 320 },
  },
  [ImageVariantEnum.CARD2X]: {
    [ScreenWidthEnum.BASE]: { width: 480 },
    [ScreenWidthEnum.MOBILE]: { width: 800 },
    [ScreenWidthEnum.TABLET]: { width: 960 },
  },
  [ImageVariantEnum.FULLWIDTH]: {
    [ScreenWidthEnum.BASE]: { width: 480 },
    [ScreenWidthEnum.MOBILE]: { width: 800 },
    [ScreenWidthEnum.TABLET]: { width: 1200 },
    [ScreenWidthEnum.DESKTOP]: { width: 1920 },
  },
  [ImageVariantEnum.GRID]: {
    [ScreenWidthEnum.BASE]: { width: 480 },
    [ScreenWidthEnum.IPAD_LANDSCAPE]: { width: 320 },
  },
  [ImageVariantEnum.HERO]: {
    [ScreenWidthEnum.BASE]: { ar: "16:9", height: 224 },
    [ScreenWidthEnum.SMALL_TABLET]: { ar: "16:9", height: 288 },
    [ScreenWidthEnum.TABLET]: { ar: "16:9", height: 320 },
    [ScreenWidthEnum.DESKTOP]: { ar: "16:9", height: 384 },
  },
  [ImageVariantEnum.LIGHTBOX]: {
    [ScreenWidthEnum.BASE]: { width: 480 },
    [ScreenWidthEnum.MOBILE]: { width: 800 },
    [ScreenWidthEnum.TABLET]: { width: 960 },
  },
  [ImageVariantEnum.PRIMARY]: {
    [ScreenWidthEnum.BASE]: { width: 480 },
    [ScreenWidthEnum.MOBILE]: { width: 600 },
    [ScreenWidthEnum.TABLET]: { width: 800 },
  },
  [ImageVariantEnum.RADIO]: {
    [ScreenWidthEnum.BASE]: { width: 80 },
    [ScreenWidthEnum.MOBILE]: { width: 120 },
    [ScreenWidthEnum.TABLET]: { width: 160 },
  },
  [ImageVariantEnum.SECONDARY]: {
    [ScreenWidthEnum.BASE]: { width: 480 },
    [ScreenWidthEnum.MOBILE]: { width: 240 },
    [ScreenWidthEnum.SMALL_TABLET]: { width: 360 },
  },
  [ImageVariantEnum.SEARCH]: {
    [ScreenWidthEnum.BASE]: { width: 480 },
    [ScreenWidthEnum.MOBILE]: { height: 240, width: 240 },
  },
  [ImageVariantEnum.SUMMARY]: {
    [ScreenWidthEnum.BASE]: { width: 184 },
  },
  [ImageVariantEnum.THUMBNAIL]: {
    [ScreenWidthEnum.BASE]: { width: 80 },
  },
};

export const IMAGE_TYPES = ["webp", "jpg"];

export const LOADING_MESSAGES: Record<string, string> = {
  // messages are for _after_ page denoted by key
  [BOOKING.AUTOTIMES]: "Securing your tee times. This won't take long",
  [BOOKING.DATE]: "Fetching available tee times for your trip",
  [BOOKING.DETAILS]: "Preparing the secure payment screen",
  [BOOKING.PAX]: "Checking availability for your selected dates",
  [BOOKING.PAYMENT]: "We're just finalising your booking",
  [BOOKING.ROOMS]: "We're finding you the best rooms",
  [BOOKING.ROOT]: "Please wait",
  [BOOKING.SIMPLE_TEE_TIMES]: "Saving your tee times. This won't take long",
  [BOOKING.TIMES]: "Securing your tee times. This won't take long",
};

export const ARRIVAL_DATE_PARAM = "arrival_date";
export const PACKAGE_ID_PARAM = "package_id";
export const PAX_PARAM = "pax";
export const PROFILE_PARAM = "profile";

export const DEFAULT_PAX = 2;
export const PHONE_NUMBER = "0800 043 6644";
export const PHONE_NUMBER_HREF = "+448000436644";
export const TRANSPARENT =
  "data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==";
